import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppHeader } from '../contexts/AppHeaderContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, RefreshCw, Loader2, MapPin, Clock, TrendingUp, TrendingDown, HeartPulse,
  Heart, Flame, Mountain, Activity, Timer, Zap, ChevronDown, Trash2,
  ChevronUp, ChevronLeft, ChevronRight, Gauge, Wind, GitCompare, MapPinned, ExternalLink, Scale, Sparkles,
  Eye, Pencil, Minus, Image as ImageIcon, Download, MessageSquare, Settings2,
} from 'lucide-react';
import api from '../utils/axiosConfig';
import { fetchStravaActivityWithStreams } from '../utils/stravaActivityApi';
import { computeKpis, buildKpiCardValueMap } from '../utils/activityKpis';
import { buildDerivedKpiRowTrends } from '../utils/activityDerivedKpiTrend';
import { getActivityTypeBadges, isWeightTrainingActivity } from '../utils/activityUtils';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, formatDistance } from 'date-fns';
import { nb } from 'date-fns/locale';
import ActivityMap from '../components/ActivityMap';
import ActivityFindSimilarModal from '../components/ActivityFindSimilarModal';
import ActivitySkillStimulusCard from '../components/ActivitySkillStimulusCard';
import StreamCharts from '../components/StreamCharts';
import RunningWorkoutViz from '../components/training/RunningWorkoutViz';
import RunningExerciseCard from '../components/training/RunningExerciseCard';
import RunningExerciseDraftPreview from '../components/training/RunningExerciseDraftPreview';
import RunningExerciseDetail from '../components/training/RunningExerciseDetail';
import UserRunningExerciseEditorModal from '../components/training/UserRunningExerciseEditorModal';
import ActivitySessionFeedback from '../components/ActivitySessionFeedback';
import SessionShareImagePresetModal from '../components/SessionShareImagePresetModal';
import KpiTrendModal from '../components/KpiTrendModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { mapActivityRowToSimilarCard } from '../utils/mapActivityRowToSimilarCard';

/** OpenAI image gen can exceed default axios 30s; server caps at 300s (SESSION_SHARE_IMAGE_TIMEOUT_MS). */
const SHARE_IMAGE_GENERATE_TIMEOUT_MS = 300000;
/** Preview fetch can be multi-MB PNG; raw img src bypasses axios and the default 30s timeout. */
const SHARE_IMAGE_PREVIEW_FETCH_MS = 120000;

const AthleteHealthBodyMapWidget = lazy(() =>
  import('../components/Widgets/Health/AthleteHealthBodyMapWidget')
);
const AgentChatWidget = lazy(() => import('../components/Widgets/AgentChatWidget'));

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { setHeaderBackButton } = useAppHeader();

  useEffect(() => {
    setHeaderBackButton(true);
    return () => setHeaderBackButton(false);
  }, [setHeaderBackButton]);
  const [data, setData] = useState(null);
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [kpiModalKpi, setKpiModalKpi] = useState('re');
  const [kpiModalActivityId, setKpiModalActivityId] = useState(null);
  const [kpiModalCardValues, setKpiModalCardValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [showAllLaps, setShowAllLaps] = useState(false);
  const [lapsViewSource, setLapsViewSource] = useState('device');
  const [splitUnit, setSplitUnit] = useState('metric');
  const [similarOpen, setSimilarOpen] = useState(false);
  const [similarActivities, setSimilarActivities] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareActivity, setCompareActivity] = useState(null);
  const [compareIndex, setCompareIndex] = useState(0);
  const [compareLoading, setCompareLoading] = useState(false);
  const [commentaryGenerating, setCommentaryGenerating] = useState(false);
  const [commentaryExpanded, setCommentaryExpanded] = useState(false);
  const [plannedOverlay, setPlannedOverlay] = useState(null);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveSubmitting, setSaveSubmitting] = useState(false);
  const [unlinkSubmitting, setUnlinkSubmitting] = useState(false);
  const [linkedWorkoutViewOpen, setLinkedWorkoutViewOpen] = useState(false);
  const [linkedWorkoutEditOpen, setLinkedWorkoutEditOpen] = useState(false);
  const [linkBusyKey, setLinkBusyKey] = useState(null);
  const [workoutSuggestions, setWorkoutSuggestions] = useState(null);
  const [workoutSuggestionsLoading, setWorkoutSuggestionsLoading] = useState(false);
  const [exerciseActionError, setExerciseActionError] = useState(null);
  const [kpiBaseline, setKpiBaseline] = useState(null);
  const [exerciseMatchExpanded, setExerciseMatchExpanded] = useState(false);
  const [healthPainOpen, setHealthPainOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [shareImageWorking, setShareImageWorking] = useState(false);
  const [shareImageIncludePeople, setShareImageIncludePeople] = useState(false);
  /** Inline only — do not reuse global `error` or share failures replace the whole page */
  const [shareImageActionError, setShareImageActionError] = useState(null);
  const [shareImagePreviewUrl, setShareImagePreviewUrl] = useState(null);
  const [shareImagePreviewLoading, setShareImagePreviewLoading] = useState(false);
  const [shareImagePreviewError, setShareImagePreviewError] = useState(false);
  const [shareImagePresetModalOpen, setShareImagePresetModalOpen] = useState(false);
  const [sessionChatModalOpen, setSessionChatModalOpen] = useState(false);
  const [activityEditOpen, setActivityEditOpen] = useState(false);
  const [activityEditName, setActivityEditName] = useState('');
  const [activityEditDescription, setActivityEditDescription] = useState('');
  const [activityEditIsRace, setActivityEditIsRace] = useState(false);
  const [activityEditSaving, setActivityEditSaving] = useState(false);
  const [activityEditError, setActivityEditError] = useState(null);

  useEffect(() => {
    setShareImageActionError(null);
  }, [id]);

  /** Blob URL via axios (cookies + long timeout). A plain img src does not use this client and is harder to diagnose (502/404). */
  useEffect(() => {
    const shareUrl = data?.share_image_url;
    const ver = data?.share_image_version;
    const activityRowId = data?.activity?.id;
    if (!id || !shareUrl || String(activityRowId) !== String(id)) {
      setShareImagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setShareImagePreviewLoading(false);
      setShareImagePreviewError(false);
      return undefined;
    }
    let cancelled = false;
    setShareImagePreviewLoading(true);
    setShareImagePreviewError(false);
    const v = ver != null && String(ver) !== '' ? String(ver) : '1';
    api
      .get(`/api/strava/activity/${id}/share-image`, {
        params: { v },
        responseType: 'blob',
        timeout: SHARE_IMAGE_PREVIEW_FETCH_MS,
      })
      .then((res) => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(res.data);
        setShareImagePreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return blobUrl;
        });
        setShareImagePreviewLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setShareImagePreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setShareImagePreviewLoading(false);
        setShareImagePreviewError(true);
      });
    return () => {
      cancelled = true;
      setShareImagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [id, data?.activity?.id, data?.share_image_url, data?.share_image_version]);

  useEffect(() => {
    const name = data?.activity?.name;
    if (name) setSaveTitle(name);
  }, [data?.activity?.name]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api
      .get(`/api/strava/activity/${id}/planned-segments`)
      .then((res) => {
        if (!cancelled) setPlannedOverlay(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setPlannedOverlay(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const plannedBoundarySecs = useMemo(() => {
    if (!plannedOverlay?.overlays?.length) return [];
    const set = new Set();
    for (const o of plannedOverlay.overlays) {
      if (o.start_time_offset_sec > 0) set.add(o.start_time_offset_sec);
    }
    return [...set].sort((a, b) => a - b);
  }, [plannedOverlay]);

  const fetchActivity = useCallback(async (options = { showLoading: true }) => {
    const showLoading = options?.showLoading !== false;
    const suppressError = options?.suppressError === true;
    if (showLoading) {
      setLoading(true);
    }
    if (!suppressError) {
      setError(null);
    }
    try {
      const next = await fetchStravaActivityWithStreams(id);
      setData(next);
      return next;
    } catch (err) {
      if (!suppressError) {
        setError(err.response?.data?.error || 'Failed to load activity');
      }
      return null;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    setExerciseMatchExpanded(false);
  }, [id]);

  useEffect(() => {
    setLapsViewSource('device');
  }, [id]);

  useEffect(() => {
    const pl = data?.planned_laps;
    if (Array.isArray(pl) && pl.length > 0) {
      setLapsViewSource('workout');
    }
  }, [data?.planned_laps]);

  useEffect(() => {
    if (!id || !data?.activity) {
      setWorkoutSuggestions(null);
      return undefined;
    }
    const a = data.activity;
    if (!a.detail_synced || isWeightTrainingActivity(a) || a.user_running_exercise_id) {
      setWorkoutSuggestions(null);
      return undefined;
    }
    let cancelled = false;
    setWorkoutSuggestionsLoading(true);
    api
      .get(`/api/strava/activity/${id}/workout-suggestions`)
      .then((res) => {
        if (!cancelled) setWorkoutSuggestions(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setWorkoutSuggestions(null);
      })
      .finally(() => {
        if (!cancelled) setWorkoutSuggestionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, data?.activity?.detail_synced, data?.activity?.user_running_exercise_id, data?.activity]);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    api
      .get(`/api/strava/activity/${id}/kpi-month-baseline`)
      .then((res) => {
        if (!cancelled) setKpiBaseline(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setKpiBaseline(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);
  useEffect(() => {
    if (!data?.activity_ai?.postprocess_status) return undefined;
    const st = data.activity_ai.postprocess_status;
    if (st !== 'pending' && st !== 'running') return undefined;
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      if (n > 36) {
        clearInterval(iv);
        return;
      }
      fetchActivity({ showLoading: false });
    }, 5000);
    return () => clearInterval(iv);
  }, [data?.activity_ai?.postprocess_status, fetchActivity, id]);



  const handleFindSimilar = () => setSimilarOpen(true);

  const handleCompareFromSuggested = useCallback(async (activity, similarList) => {
    setSimilarActivities(similarList);
    const idx = similarList.findIndex((x) => x.id === activity.id);
    setCompareIndex(idx >= 0 ? idx : 0);
    setCompareOpen(true);
    setCompareLoading(true);
    try {
      const data = await fetchStravaActivityWithStreams(activity.id);
      setCompareActivity(data);
    } catch (err) {
      setCompareActivity(null);
    } finally {
      setCompareLoading(false);
    }
  }, []);

  const handleCompareFromBrowse = useCallback(async (row, chain) => {
    const mapped = chain.map(mapActivityRowToSimilarCard);
    setSimilarActivities(mapped);
    const idx = mapped.findIndex((x) => x.id === row.id);
    setCompareIndex(idx >= 0 ? idx : 0);
    setCompareOpen(true);
    setCompareLoading(true);
    try {
      const data = await fetchStravaActivityWithStreams(row.id);
      setCompareActivity(data);
    } catch (err) {
      setCompareActivity(null);
    } finally {
      setCompareLoading(false);
    }
  }, []);

  const handleOpenCompare = async (similarActivity) => {
    const idx = similarActivities.findIndex((a) => a.id === similarActivity.id);
    setCompareIndex(idx >= 0 ? idx : 0);
    setCompareOpen(true);
    setCompareLoading(true);
    try {
      const data = await fetchStravaActivityWithStreams(similarActivity.id);
      setCompareActivity(data);
    } catch (err) {
      setCompareActivity(null);
    } finally {
      setCompareLoading(false);
    }
  };

  const handleComparePrev = () => {
    if (compareIndex > 0) handleOpenCompare(similarActivities[compareIndex - 1]);
  };

  const handleCompareNext = () => {
    if (compareIndex < similarActivities.length - 1) handleOpenCompare(similarActivities[compareIndex + 1]);
  };

  const handleResync = async () => {
    setSyncing(true);
    try {
      await api.post(`/api/strava/sync-detail/${id}`);
      await fetchActivity();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to re-sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteActivity = async () => {
    setDeletingActivity(true);
    setDeleteError(null);
    try {
      await api.delete(`/api/strava/activity/${id}`);
      setDeleteDialogOpen(false);
      navigate('/dashboard');
    } catch (err) {
      setDeleteError(err.response?.data?.error || t('activityDetail.deleteFailed'));
    } finally {
      setDeletingActivity(false);
    }
  };

  const handleGenerateShareImage = async (genOpts = {}) => {
    setShareImageActionError(null);
    setShareImageWorking(true);
    try {
      const ephemeralFiles = Array.isArray(genOpts.ephemeralFiles) ? genOpts.ephemeralFiles : [];
      const hasEphemeral = ephemeralFiles.length > 0;
      let res;
      if (hasEphemeral) {
        const fd = new FormData();
        fd.append('includePeople', shareImageIncludePeople ? 'true' : 'false');
        if (genOpts.presetId != null && Number.isFinite(Number(genOpts.presetId)) && Number(genOpts.presetId) > 0) {
          fd.append('presetId', String(genOpts.presetId));
        }
        if (genOpts.customPrompt !== undefined && genOpts.customPrompt !== null) {
          fd.append('customPrompt', String(genOpts.customPrompt));
        }
        for (const f of ephemeralFiles) {
          if (f instanceof File) fd.append('referenceImages', f);
        }
        res = await api.post(`/api/strava/activity/${id}/share-image`, fd, {
          timeout: SHARE_IMAGE_GENERATE_TIMEOUT_MS,
        });
      } else {
        const body = { includePeople: shareImageIncludePeople };
        if (genOpts.presetId != null && Number.isFinite(Number(genOpts.presetId)) && Number(genOpts.presetId) > 0) {
          body.presetId = Number(genOpts.presetId);
        }
        if (genOpts.customPrompt !== undefined && genOpts.customPrompt !== null) {
          body.customPrompt = String(genOpts.customPrompt);
        }
        res = await api.post(`/api/strava/activity/${id}/share-image`, body, {
          timeout: SHARE_IMAGE_GENERATE_TIMEOUT_MS,
        });
      }
      const refreshed = await fetchActivity({ showLoading: false, suppressError: true });
      if (!refreshed && res.data?.share_image_url) {
        setData((prev) =>
          prev
            ? {
                ...prev,
                share_image_url: res.data.share_image_url,
                share_image_version: res.data.version ?? prev.share_image_version,
              }
            : prev
        );
      }
      return true;
    } catch (err) {
      const timedOut =
        err.code === 'ECONNABORTED' ||
        (typeof err.message === 'string' && /timeout/i.test(err.message));
      setShareImageActionError(
        timedOut
          ? t('activityDetail.sessionShareImageTimeout')
          : err.response?.data?.error ||
              err.message ||
              t('activityDetail.sessionShareImageError')
      );
      return false;
    } finally {
      setShareImageWorking(false);
    }
  };

  const handleRemoveShareImage = async () => {
    if (!window.confirm(t('activityDetail.sessionShareImageRemoveConfirm'))) return;
    setShareImageActionError(null);
    setShareImageWorking(true);
    try {
      await api.delete(`/api/strava/activity/${id}/share-image`);
      const refreshed = await fetchActivity({ showLoading: false, suppressError: true });
      if (!refreshed) {
        setData((prev) => (prev ? { ...prev, share_image_url: null, share_image_version: null } : prev));
      }
    } catch (err) {
      setShareImageActionError(
        err.response?.data?.error ||
          err.message ||
          t('activityDetail.sessionShareImageError')
      );
    } finally {
      setShareImageWorking(false);
    }
  };

  const handleDownloadShareImage = async () => {
    setShareImageActionError(null);
    try {
      const v = data?.share_image_version ?? '1';
      const res = await api.get(`/api/strava/activity/${id}/share-image?download=1&v=${encodeURIComponent(v)}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zitius-session-${id}-v${v}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setShareImageActionError(
        err.response?.data?.error ||
          err.message ||
          t('activityDetail.sessionShareImageError')
      );
    }
  };

  const handleGenerateCommentary = async () => {
    setCommentaryGenerating(true);
    try {
      await api.post(`/api/strava/activity/${id}/regenerate-commentary`);
      await fetchActivity();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate commentary');
    } finally {
      setCommentaryGenerating(false);
    }
  };

  const handleLinkExerciseCard = async (urexId) => {
    setExerciseActionError(null);
    setLinkBusyKey(`urex-${urexId}`);
    try {
      await api.post(`/api/strava/activity/${id}/link-exercise`, { user_running_exercise_id: urexId });
      await fetchActivity({ showLoading: false });
      const res = await api.get(`/api/strava/activity/${id}/planned-segments`);
      setPlannedOverlay(res.data || null);
    } catch (err) {
      setExerciseActionError(err.response?.data?.error || 'Failed to link workout');
    } finally {
      setLinkBusyKey(null);
    }
  };

  const handleLinkLibrarySession = async (librarySessionId) => {
    setExerciseActionError(null);
    setLinkBusyKey(`lib-${librarySessionId}`);
    try {
      await api.post(`/api/strava/activity/${id}/link-exercise`, {
        library_training_session_id: librarySessionId,
      });
      await fetchActivity({ showLoading: false });
      const res = await api.get(`/api/strava/activity/${id}/planned-segments`);
      setPlannedOverlay(res.data || null);
    } catch (err) {
      setExerciseActionError(err.response?.data?.error || 'Failed to link workout');
    } finally {
      setLinkBusyKey(null);
    }
  };

  const handleSaveAsExercise = async (draft, options = {}) => {
    if (!draft?.segment_payloads?.length) return;
    const { addToPlannerLibrary = true, linkActivity = true } = options;
    setExerciseActionError(null);
    setSaveSubmitting(true);
    try {
      await api.post('/api/user-running-exercises/from-activity', {
        activity_id: parseInt(id, 10),
        title: saveTitle.trim() || undefined,
        description: saveDescription.trim() || undefined,
        segments: draft.segment_payloads,
        add_to_planner_library: addToPlannerLibrary,
        link_activity: linkActivity,
      });
      await fetchActivity({ showLoading: false });
      const ps = await api.get(`/api/strava/activity/${id}/planned-segments`);
      setPlannedOverlay(ps.data || null);
      setExerciseMatchExpanded(true);
    } catch (err) {
      setExerciseActionError(err.response?.data?.error || 'Failed to save exercise');
    } finally {
      setSaveSubmitting(false);
    }
  };

  const handleUnlinkExercise = async () => {
    setExerciseActionError(null);
    setUnlinkSubmitting(true);
    try {
      await api.post(`/api/strava/activity/${id}/unlink-exercise`);
      await fetchActivity({ showLoading: false });
      const ps = await api.get(`/api/strava/activity/${id}/planned-segments`);
      setPlannedOverlay(ps.data || null);
    } catch (err) {
      setExerciseActionError(err.response?.data?.error || 'Failed to unlink workout');
    } finally {
      setUnlinkSubmitting(false);
    }
  };

  const handleLinkedWorkoutSaved = useCallback(async () => {
    await fetchActivity({ showLoading: false });
    try {
      const ps = await api.get(`/api/strava/activity/${id}/planned-segments`);
      setPlannedOverlay(ps.data || null);
    } catch {
      setPlannedOverlay(null);
    }
  }, [fetchActivity, id]);

  const openActivityEdit = useCallback(() => {
    const act = data?.activity;
    if (!act) return;
    setActivityEditName(String(act.name || '').trim());
    setActivityEditDescription(act.description != null ? String(act.description) : '');
    setActivityEditIsRace(Number(act.workout_type) === 1);
    setActivityEditError(null);
    setActivityEditOpen(true);
  }, [data?.activity]);

  const saveActivityEdit = useCallback(async () => {
    const name = activityEditName.trim();
    if (!name) {
      setActivityEditError(t('activityDetail.editTitleRequired'));
      return;
    }
    setActivityEditSaving(true);
    setActivityEditError(null);
    try {
      const res = await api.put(`/api/strava/activity/${id}`, {
        name,
        description: activityEditDescription,
        isRace: activityEditIsRace,
      });
      setActivityEditOpen(false);
      await fetchActivity({ showLoading: false });
      if (res.data?.goldenZonesRefreshed) {
        window.dispatchEvent(new Event('goldenZonesUpdated'));
      }
      const detailMsg = res.data?.stravaUpdated
        ? t('activityDetail.editStravaSynced')
        : res.data?.stravaSkipReason === 'no_activity_write_scope'
          ? t('activityDetail.editStravaScopeMissing')
          : res.data?.stravaError || res.data?.stravaSkipReason
            ? t('activityDetail.editStravaNotSynced')
            : '';
      if (detailMsg) {
        window.alert(`${t('activityDetail.editSaved')}\n${detailMsg}`);
      }
    } catch (err) {
      setActivityEditError(err.response?.data?.error || t('common.error'));
    } finally {
      setActivityEditSaving(false);
    }
  }, [
    activityEditDescription,
    activityEditIsRace,
    activityEditName,
    fetchActivity,
    id,
    t,
  ]);

  useEffect(() => {
    if (!data?.linked_running_exercise?.id) {
      setLinkedWorkoutViewOpen(false);
      setLinkedWorkoutEditOpen(false);
    }
  }, [data?.linked_running_exercise?.id]);

  const derivedKpiRowTrends = useMemo(() => {
    if (!data?.activity) return {};
    const kpis = computeKpis(data.activity, t);
    return buildDerivedKpiRowTrends(kpis, kpiBaseline, t);
  }, [data?.activity, kpiBaseline, t]);

  if (loading) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='text-center'>
          <Loader2 className='h-8 w-8 animate-spin text-primary mx-auto mb-3' />
          <p className='text-sm text-muted-foreground'>{t('activityDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-sm text-destructive mb-4'>{error}</p>
          <Button variant='outline' onClick={() => navigate('/dashboard')}>
            <ArrowLeft className='h-4 w-4 mr-2' /> {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  const {
    activity,
    share_image_url: shareImageUrl,
    share_image_version: shareImageVersion,
    laps,
    planned_laps: plannedLaps,
    splits,
    streams,
    segmentEfforts,
    dna,
    families,
    activity_ai: activityAi,
    linked_running_exercise: linkedRunningExercise,
    stryd_instance_workout: strydInstanceWorkout,
  } = data;

  const hasPlannedLapsView = Array.isArray(plannedLaps) && plannedLaps.length > 0;
  const displayLaps =
    hasPlannedLapsView && lapsViewSource === 'workout' ? plannedLaps : laps || [];

  const isWeightTraining = isWeightTrainingActivity(activity);
  const hasRouteMap = !!(activity.polyline || activity.summary_polyline);
  const showSeparatorBeforeDetailBlocks = !isWeightTraining || hasRouteMap;

  return (
    <div className='min-h-screen bg-background'>
      <div className='max-w-5xl mx-auto px-4 py-6'>
        {/* Page actions */}
        <div className='flex items-center justify-end gap-2 mb-6'>
          <Button
            variant='outline'
            size='sm'
            onClick={openActivityEdit}
            title={t('activityDetail.editHint')}
          >
            <Pencil className='h-4 w-4 mr-2' />
            {t('activityDetail.edit')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setHealthPainOpen(true)}
            title={t('activityDetail.logPainHint')}
          >
            <HeartPulse className='h-4 w-4 mr-2' />
            {t('activityDetail.logPain')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleFindSimilar}
            title={t('activityDetail.findSimilarHint')}
          >
            <GitCompare className='h-4 w-4 mr-2' />
            {t('activityDetail.findSimilar')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleResync}
            disabled={syncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? t('activityDetail.resyncing') : t('activityDetail.resync')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            className='border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive'
            onClick={() => {
              setDeleteError(null);
              setDeleteDialogOpen(true);
            }}
            disabled={syncing || deletingActivity}
            title={t('activityDetail.deleteHint')}
          >
            <Trash2 className='h-4 w-4 mr-2' />
            {t('activityDetail.delete')}
          </Button>
        </div>

        {/* Activity title */}
        <div className='mb-6'>
          <div className='flex flex-wrap items-center gap-2 mb-2'>
            <h1 className='text-2xl font-bold text-foreground'>{activity.name}</h1>
            {(() => {
              const { mainLabel, mainColor, extraBadges } = getActivityTypeBadges(activity, t);
              return (
                <>
                  <Badge variant='outline' className={mainColor}>
                    {mainLabel}
                  </Badge>
                  {extraBadges.map((b) => (
                    <Badge key={b.label} variant='outline' className={`text-xs ${b.className}`}>
                      {b.label}
                    </Badge>
                  ))}
                </>
              );
            })()}
          </div>
          <p className='text-sm text-muted-foreground'>
            {formatFullDate(activity.start_date_local || activity.start_date)}
            {activity.timezone && ` · ${activity.timezone}`}
          </p>
          {(dna?.location?.start_place_name || dna?.location?.region_name) && (
            <p className='text-sm text-muted-foreground flex items-center gap-1.5 mt-1'>
              <MapPinned className='h-4 w-4 shrink-0' />
              {dna.location.start_place_name && dna.location.end_place_name && dna.location.start_place_name !== dna.location.end_place_name
                ? `${dna.location.start_place_name} → ${dna.location.end_place_name}`
                : dna.location.start_place_name || dna.location.region_name}
              {dna.location.region_name && dna.location.start_place_name && !dna.location.start_place_name.includes(dna.location.region_name) && ` · ${dna.location.region_name}`}
            </p>
          )}
          {families && families.length > 0 && (
            <div className='flex flex-wrap gap-1.5 mt-2'>
              {families.map((f) => (
                <Badge key={f.family_key} variant='secondary' className='text-xs'>
                  {f.name}
                </Badge>
              ))}
            </div>
          )}
          {activity.description && (
            <p className='text-sm text-foreground/80 mt-2'>{activity.description}</p>
          )}
        </div>

        {/* KPI Row */}
        <KpiRow
          activity={activity}
          t={t}
          trends={derivedKpiRowTrends}
          onKpiClick={(key) => {
            setKpiModalKpi(key);
            setKpiModalActivityId(activity?.id);
            setKpiModalCardValues(buildKpiCardValueMap(activity, t));
            setKpiModalOpen(true);
          }}
        />

        <ActivitySessionFeedback
          key={activity.id}
          activityId={activity.id}
          funFactor={activity.fun_factor}
          sessionRpe={activity.session_rpe}
          onSaved={() => fetchActivity({ showLoading: false })}
        />

        <Card className='mb-6 border-border'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-base flex items-center gap-2'>
              <ImageIcon className='h-5 w-5 text-primary' />
              {t('activityDetail.sessionShareImage')}
            </CardTitle>
            <CardDescription>{t('activityDetail.sessionShareImageDesc')}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div
              className={
                shareImageUrl
                  ? 'grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 md:gap-6 items-start'
                  : undefined
              }
            >
              {shareImageUrl && (
                <div className='min-w-0 rounded-lg overflow-hidden border border-border bg-muted/30 min-h-[10rem] flex items-center justify-center relative'>
                  {shareImagePreviewLoading && (
                    <Loader2 className='h-8 w-8 animate-spin text-primary' aria-hidden />
                  )}
                  {shareImagePreviewError && !shareImagePreviewLoading && (
                    <p className='text-sm text-destructive px-3 text-center' role='alert'>
                      {t('activityDetail.sessionShareImagePreviewError')}
                    </p>
                  )}
                  {shareImagePreviewUrl && (
                    <img
                      src={shareImagePreviewUrl}
                      alt=''
                      className='w-full max-h-72 object-cover md:max-h-[min(30rem,70vh)] md:object-contain'
                    />
                  )}
                </div>
              )}
              <div
                className={
                  shareImageUrl
                    ? 'flex flex-col gap-3 w-full md:w-52 xl:w-60 shrink-0'
                    : 'flex flex-col gap-3'
                }
              >
                {!shareImageUrl && (
                  <div className='flex flex-wrap items-center gap-3'>
                    <div className='flex items-center gap-2'>
                      <Switch
                        id='share-img-people'
                        checked={shareImageIncludePeople}
                        onCheckedChange={setShareImageIncludePeople}
                        disabled={shareImageWorking}
                      />
                      <Label htmlFor='share-img-people' className='text-sm cursor-pointer'>
                        {t('activityDetail.sessionShareImageIncludePeopleHint')}
                      </Label>
                    </div>
                  </div>
                )}
                <div
                  className={
                    shareImageUrl
                      ? 'flex flex-col gap-2 items-stretch [&_button]:w-full'
                      : 'flex flex-wrap gap-2'
                  }
                >
                  <Button
                    type='button'
                    variant='secondary'
                    size='sm'
                    disabled={shareImageWorking}
                    onClick={(e) => {
                      e.preventDefault();
                      handleGenerateShareImage();
                    }}
                  >
                    {shareImageWorking ? (
                      <Loader2 className='h-4 w-4 animate-spin mr-2' />
                    ) : null}
                    {shareImageUrl
                      ? t('activityDetail.sessionShareImageRegenerate')
                      : t('activityDetail.sessionShareImageGenerate')}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    disabled={shareImageWorking}
                    onClick={() => setShareImagePresetModalOpen(true)}
                    className='inline-flex items-center gap-2'
                  >
                    <Settings2 className='h-4 w-4' />
                    {t('activityDetail.sessionShareImageCustomize')}
                  </Button>
                  {shareImageUrl && (
                    <>
                      <Button type='button' variant='outline' size='sm' onClick={handleDownloadShareImage}>
                        <Download className='h-4 w-4 mr-2' />
                        {t('activityDetail.sessionShareImageDownload')}
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='text-destructive border-destructive/40 hover:bg-destructive/10'
                        disabled={shareImageWorking}
                        onClick={handleRemoveShareImage}
                      >
                        {t('activityDetail.sessionShareImageRemove')}
                      </Button>
                    </>
                  )}
                </div>
                {shareImageUrl && (
                  <div className='flex items-start gap-2'>
                    <Switch
                      id='share-img-people'
                      checked={shareImageIncludePeople}
                      onCheckedChange={setShareImageIncludePeople}
                      disabled={shareImageWorking}
                      className='mt-0.5 shrink-0'
                    />
                    <Label
                      htmlFor='share-img-people'
                      className='text-xs text-muted-foreground leading-snug cursor-pointer font-normal'
                    >
                      {t('activityDetail.sessionShareImageIncludePeopleHint')}
                    </Label>
                  </div>
                )}
              </div>
            </div>
            {shareImageActionError && (
              <p className='text-sm text-destructive' role='alert'>
                {shareImageActionError}
              </p>
            )}
          </CardContent>
        </Card>

        <SessionShareImagePresetModal
          open={shareImagePresetModalOpen}
          onOpenChange={setShareImagePresetModalOpen}
          shareImageWorking={shareImageWorking}
          actionError={shareImagePresetModalOpen ? shareImageActionError : null}
          t={t}
          onGenerate={async (payload) => {
            const ok = await handleGenerateShareImage(payload);
            if (ok) setShareImagePresetModalOpen(false);
          }}
        />

        {/* Key stats grid — omit metrics with no valid numeric value (e.g. distance for indoor strength) */}
        <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6'>
          {(() => {
            const m = parseFloat(activity.distance);
            return Number.isFinite(m) && m > 0 ? (
              <StatCard icon={<MapPin />} label={t('athlete.distance')} value={formatDistanceKm(activity.distance)} />
            ) : null;
          })()}
          {(() => {
            const mt = parseFloat(activity.moving_time);
            return Number.isFinite(mt) && mt > 0 ? (
              <StatCard icon={<Clock />} label={t('athlete.duration')} value={formatDuration(activity.moving_time)} />
            ) : null;
          })()}
          {(() => {
            const sp = parseFloat(activity.average_speed);
            return Number.isFinite(sp) && sp > 0 ? (
              <StatCard icon={<Gauge />} label={t('athlete.pace')} value={formatPace(activity.average_speed)} />
            ) : null;
          })()}
          {(() => {
            const hr = parseFloat(activity.average_heartrate);
            return activity.has_heartrate && Number.isFinite(hr) && hr > 0 ? (
              <StatCard icon={<Heart />} label={t('athlete.heartRate')} value={`${Math.round(hr)} bpm`} />
            ) : null;
          })()}
          {(() => {
            const w = parseFloat(activity.average_watts);
            return Number.isFinite(w) && w > 0 ? (
              <StatCard
                icon={<Zap />}
                label={t('activityDetail.avgPower')}
                value={`${Math.round(w)} ${t('units.powerW')}`}
              />
            ) : null;
          })()}
          {(() => {
            const eg = parseFloat(activity.total_elevation_gain);
            return Number.isFinite(eg) && eg > 0 ? (
              <StatCard icon={<Mountain />} label={t('athlete.elevation')} value={formatElevation(activity.total_elevation_gain)} />
            ) : null;
          })()}
          {(() => {
            const c = parseFloat(activity.calories);
            return Number.isFinite(c) && c > 0 ? (
              <StatCard icon={<Flame />} label={t('athlete.calories')} value={`${Math.round(c)}`} />
            ) : null;
          })()}
          {(() => {
            const s = parseFloat(activity.suffer_score);
            return Number.isFinite(s) && s > 0 ? (
              <StatCard icon={<HeartPulse />} label={t('activityDetail.sufferScore')} value={String(Math.round(s))} />
            ) : null;
          })()}
        </div>

        {/* Additional stats */}
        <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6'>
          <MiniStat label={t('activityDetail.elapsedTime')} value={formatDuration(activity.elapsed_time)} />
          <MiniStat label={t('activityDetail.maxSpeed')} value={formatPace(activity.max_speed)} />
          {activity.max_heartrate && (
            <MiniStat label={t('activityDetail.maxHr')} value={`${Math.round(parseFloat(activity.max_heartrate))} bpm`} />
          )}
          {activity.average_cadence && (
            <MiniStat label={t('activityDetail.cadence')} value={`${Math.round(parseFloat(activity.average_cadence) * 2)} spm`} />
          )}
          {activity.device_name && (
            <MiniStat label={t('activityDetail.device')} value={activity.device_name} />
          )}
          {activity.gear_id && (
            <MiniStat label={t('activityDetail.gear')} value={activity.gear_name || activity.gear_id} />
          )}
          <MiniStat label={t('activityDetail.kudos')} value={activity.kudos_count ?? 0} />
          <MiniStat label={t('activityDetail.elevation')} value={formatElevation(activity.total_elevation_gain)} />
          {activity.estimated_vo2max != null && (
            <div className="col-span-2 sm:col-span-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
              <Wind className="h-4 w-4 text-primary shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">{t('vo2max.est')}</p>
                <p className="text-sm font-medium">
                  {parseFloat(activity.estimated_vo2max).toFixed(1)} mL/kg/min
                  {activity.vo2max_method && (
                    <span className="text-muted-foreground font-normal ml-1">
                      ({activity.vo2max_method === 'daniels_gilbert' ? t('vo2max.danielsGilbert') : t('vo2max.paceHr')})
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* AI Commentary */}
        {activity.ai_commentary ? (
          <div className='mb-6'>
            <div className='flex items-start justify-between gap-2 mb-3'>
              <h2 className='text-lg font-semibold text-foreground flex items-center gap-2 min-w-0'>
                <Sparkles className='h-5 w-5 text-primary shrink-0' />
                <span className='truncate'>{t('activityDetail.aiCommentary')}</span>
              </h2>
              {activity.detail_synced && (
                <div className='flex shrink-0 items-center gap-1'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-9 w-9 text-muted-foreground hover:text-foreground'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateCommentary();
                        }}
                        disabled={commentaryGenerating}
                        aria-label={t('activityDetail.regenerateCommentaryTooltip')}
                      >
                        <RefreshCw className={cn('h-4 w-4', commentaryGenerating && 'animate-spin')} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side='bottom'>{t('activityDetail.regenerateCommentaryTooltip')}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-9 w-9 text-muted-foreground hover:text-foreground'
                        onClick={() => setSessionChatModalOpen(true)}
                        aria-label={t('activityDetail.chatAboutSessionTooltip')}
                      >
                        <MessageSquare className='h-4 w-4' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side='bottom'>{t('activityDetail.chatAboutSessionTooltip')}</TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
            <div
              role='button'
              tabIndex={0}
              onClick={() => setCommentaryExpanded((e) => !e)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setCommentaryExpanded((x) => !x)}
              className={cn(
                'rounded-xl border border-border bg-muted/20 p-4 space-y-3 cursor-pointer select-none transition-all',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              )}
            >
              <div className='relative'>
                <div
                  className={cn(
                    'overflow-hidden transition-[max-height] duration-200 ease-out',
                    commentaryExpanded ? 'max-h-[60rem]' : 'max-h-[3.25rem]'
                  )}
                >
                  <div className='space-y-3'>
                    {activity.ai_commentary.split(/\n\n+/).map((para, i) => (
                      <p key={i} className='text-sm text-foreground/90 leading-relaxed'>
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
                {!commentaryExpanded && (
                  <div className='absolute bottom-0 left-0 right-0 h-8 pointer-events-none bg-gradient-to-t from-muted/20 to-transparent' />
                )}
              </div>
              <div className='flex items-center justify-end gap-1 pt-1 text-xs text-muted-foreground'>
                {commentaryExpanded ? (
                  <ChevronUp className='h-4 w-4' />
                ) : (
                  <ChevronDown className='h-4 w-4' />
                )}
                <span>{commentaryExpanded ? t('activityDetail.showLess') : t('activityDetail.showMore')}</span>
              </div>
            </div>
          </div>
        ) : activity.detail_synced &&
          (activityAi?.postprocess_status === 'pending' || activityAi?.postprocess_status === 'running') ? (
          <div className='mb-6'>
            <div className='rounded-xl border border-dashed border-border bg-muted/10 p-6 flex flex-col items-center justify-center gap-3'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
              <p className='text-sm text-muted-foreground text-center'>
                {t('activityDetail.aiStillAnalyzing')}
              </p>
              <Button variant='outline' size='sm' type='button' onClick={() => setSessionChatModalOpen(true)}>
                <MessageSquare className='h-4 w-4 mr-2' />
                {t('activityDetail.chatAboutSession')}
              </Button>
            </div>
          </div>
        ) : activity.detail_synced ? (
          <div className='mb-6'>
            <div className='rounded-xl border border-dashed border-border bg-muted/10 p-6 flex flex-col items-center justify-center gap-3'>
              <Sparkles className='h-8 w-8 text-muted-foreground' />
              <p className='text-sm text-muted-foreground text-center'>
                {t('activityDetail.commentaryNotGenerated')}
              </p>
              <div className='flex flex-wrap items-center justify-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleGenerateCommentary}
                  disabled={commentaryGenerating}
                >
                  {commentaryGenerating ? (
                    <>
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      {t('activityDetail.generatingCommentary')}
                    </>
                  ) : (
                    <>
                      <Sparkles className='h-4 w-4 mr-2' />
                      {t('activityDetail.generateCommentary')}
                    </>
                  )}
                </Button>
                <Button variant='outline' size='sm' type='button' onClick={() => setSessionChatModalOpen(true)}>
                  <MessageSquare className='h-4 w-4 mr-2' />
                  {t('activityDetail.chatAboutSession')}
                </Button>
              </div>
            </div>
          </div>
        ) : null}


        {/* Linked planned running workout */}
        {activity.detail_synced &&
          !isWeightTraining &&
          activity.user_running_exercise_id &&
          linkedRunningExercise && (
          <div className='mb-6 rounded-xl border border-border bg-card p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-foreground flex items-center gap-2'>
              <Sparkles className='h-5 w-5 text-primary' />
              {t('activityDetail.linkedWorkoutTitle')}
            </h2>
            {exerciseActionError && (
              <p className='text-sm text-destructive' role='alert'>
                {exerciseActionError}
              </p>
            )}
            <RunningExerciseCard
              title={linkedRunningExercise.title}
              description={linkedRunningExercise.description}
              splits={linkedRunningExercise.preview_splits || []}
              vizHeightPx={44}
            />
            <div className='flex flex-wrap items-center gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setLinkedWorkoutViewOpen(true)}
              >
                <Eye className='h-4 w-4 mr-2' />
                {t('activityDetail.viewLinkedWorkout')}
              </Button>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setLinkedWorkoutEditOpen(true)}
              >
                <Pencil className='h-4 w-4 mr-2' />
                {t('activityDetail.editLinkedWorkout')}
              </Button>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={unlinkSubmitting}
                onClick={handleUnlinkExercise}
              >
                {unlinkSubmitting ? <Loader2 className='h-4 w-4 animate-spin' /> : t('activityDetail.unlinkWorkout')}
              </Button>
            </div>
          </div>
        )}

        {activity.detail_synced && !isWeightTraining && !activity.user_running_exercise_id && (
          <div className='mb-6 rounded-xl border border-border bg-card p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-foreground flex items-center gap-2'>
              <Activity className='h-5 w-5 text-primary' />
              {t('activityDetail.workoutSuggestionsTitle')}
            </h2>
            <p className='text-xs text-muted-foreground'>{t('activityDetail.workoutSuggestionsHint')}</p>
            {exerciseActionError && (
              <p className='text-sm text-destructive' role='alert'>
                {exerciseActionError}
              </p>
            )}
            {workoutSuggestionsLoading ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                {t('activityDetail.workoutSuggestionsLoading')}
              </div>
            ) : (
              <>
                {(workoutSuggestions?.same_day_urex?.length ?? 0) > 0 && (
                  <div className='space-y-2'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      {t('activityDetail.workoutSuggestionsSameDay')}
                    </p>
                    <div className='grid gap-3 sm:grid-cols-1 md:grid-cols-3'>
                      {workoutSuggestions.same_day_urex.map((u) => (
                        <div
                          key={`sug-urex-${u.user_running_exercise_id}`}
                          className={cn(
                            'rounded-xl border p-3 flex flex-col gap-2',
                            u.primary_same_day
                              ? 'border-primary ring-2 ring-primary/35 bg-primary/5 shadow-sm'
                              : 'border-border bg-card'
                          )}
                        >
                          <div className='flex items-start justify-between gap-2 min-h-[1.25rem]'>
                            <p className='text-sm font-medium text-foreground truncate'>{u.title || '—'}</p>
                            {u.primary_same_day ? (
                              <Badge variant='default' className='shrink-0 text-[10px] px-1.5 py-0 h-5'>
                                {t('activityDetail.workoutSuggestionsTopMatch')}
                              </Badge>
                            ) : null}
                          </div>
                          <RunningWorkoutViz splits={u.preview_splits || []} heightPx={40} className='w-full' />
                          <Button
                            type='button'
                            size='sm'
                            className='mt-auto'
                            disabled={linkBusyKey === `urex-${u.user_running_exercise_id}`}
                            onClick={() => handleLinkExerciseCard(u.user_running_exercise_id)}
                          >
                            {linkBusyKey === `urex-${u.user_running_exercise_id}` ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              t('activityDetail.linkWorkout')
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(workoutSuggestions?.library_sessions?.length ?? 0) > 0 && (
                  <div className='space-y-2'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      {t('activityDetail.workoutSuggestionsLibrary')}
                    </p>
                    <div className='grid gap-3 sm:grid-cols-1 md:grid-cols-3'>
                      {workoutSuggestions.library_sessions.map((s) => (
                        <div
                          key={`sug-lib-${s.library_training_session_id}`}
                          className='rounded-xl border border-border bg-card p-3 flex flex-col gap-2'
                        >
                          <p className='text-sm font-medium text-foreground truncate'>{s.title || '—'}</p>
                          <RunningWorkoutViz splits={s.preview_splits || []} heightPx={40} className='w-full' />
                          <Button
                            type='button'
                            size='sm'
                            className='mt-auto'
                            disabled={linkBusyKey === `lib-${s.library_training_session_id}`}
                            onClick={() => handleLinkLibrarySession(s.library_training_session_id)}
                          >
                            {linkBusyKey === `lib-${s.library_training_session_id}` ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              t('activityDetail.linkWorkout')
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!workoutSuggestionsLoading &&
                  workoutSuggestions &&
                  !workoutSuggestions.already_linked &&
                  (workoutSuggestions.same_day_urex?.length ?? 0) === 0 &&
                  (workoutSuggestions.library_sessions?.length ?? 0) === 0 && (
                    <p className='text-sm text-muted-foreground'>{t('activityDetail.workoutSuggestionsEmpty')}</p>
                  )}
              </>
            )}
          </div>
        )}

        {/* Stryd calendar instance structure (per-activity workout JSON) */}
        {activity.detail_synced &&
          !isWeightTraining &&
          !activity.user_running_exercise_id &&
          !!activity.stryd_activity_id &&
          (strydInstanceWorkout?.preview_splits?.length ?? 0) > 0 && (
          <div className='mb-6 rounded-xl border border-border bg-card p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-foreground flex items-center gap-2'>
              <Gauge className='h-5 w-5 text-primary' />
              {t('activityDetail.strydInstanceWorkoutTitle')}
            </h2>
            <p className='text-xs text-muted-foreground'>{t('activityDetail.strydInstanceWorkoutHint')}</p>
            <RunningExerciseCard
              title={strydInstanceWorkout.title || activity.name || '—'}
              splits={strydInstanceWorkout.preview_splits}
              vizHeightPx={44}
            />
          </div>
        )}

        {/* Exercise match (ZitiusAI) */}
        {activity.detail_synced &&
          !isWeightTraining &&
          activityAi?.postprocess_status === 'complete' &&
          !activity.user_running_exercise_id && (
          <div className='mb-6 rounded-xl border border-border bg-card overflow-hidden'>
            <button
              type='button'
              className={cn(
                'flex w-full items-start justify-between gap-3 p-4 text-left transition-colors',
                'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
              )}
              onClick={() => setExerciseMatchExpanded((v) => !v)}
              aria-expanded={exerciseMatchExpanded}
              aria-controls='activity-exercise-match-panel'
              id='activity-exercise-match-trigger'
            >
              <div className='min-w-0 flex-1'>
                <h2 className='text-lg font-semibold text-foreground flex items-center gap-2'>
                  <Sparkles className='h-5 w-5 text-primary shrink-0' aria-hidden />
                  {t('activityDetail.exerciseMatchTitle')}
                </h2>
                {!exerciseMatchExpanded && (
                  <p className='text-sm text-muted-foreground mt-1'>
                    {(() => {
                      const n = activityAi?.exercise_payload?.candidates?.length || 0;
                      const hasDraft =
                        (activityAi?.exercise_payload?.save_draft?.segment_payloads?.length || 0) > 0;
                      if (n > 0) return t('activityDetail.exerciseMatchCollapsedCandidates', { count: n });
                      if (hasDraft) return t('activityDetail.exerciseMatchCollapsedNoMatch');
                      return t('activityDetail.exerciseMatchCollapsedEmpty');
                    })()}
                  </p>
                )}
              </div>
              {exerciseMatchExpanded ? (
                <ChevronUp className='h-5 w-5 shrink-0 text-muted-foreground mt-0.5' aria-hidden />
              ) : (
                <ChevronDown className='h-5 w-5 shrink-0 text-muted-foreground mt-0.5' aria-hidden />
              )}
            </button>
            {exerciseMatchExpanded && (
              <div
                id='activity-exercise-match-panel'
                role='region'
                aria-labelledby='activity-exercise-match-trigger'
                className='border-t border-border px-4 pb-4 pt-3 space-y-4'
              >
                {exerciseActionError && (
                  <p className='text-sm text-destructive' role='alert'>
                    {exerciseActionError}
                  </p>
                )}
                {activityAi?.exercise_payload?.candidates?.length > 0 && (
                  <div className='grid gap-3 sm:grid-cols-1 md:grid-cols-3'>
                    {activityAi.exercise_payload.candidates.map((c) => (
                      <div
                        key={`${c.kind}-${c.id}`}
                        className='rounded-xl border border-border bg-card p-3 flex flex-col gap-2'
                      >
                        <p className='text-sm font-medium text-foreground truncate'>{c.title || '—'}</p>
                        <RunningWorkoutViz splits={c.preview_splits || []} heightPx={40} className='w-full' />
                        <Button
                          type='button'
                          size='sm'
                          className='mt-auto'
                          disabled={linkBusyKey === `urex-${c.id}`}
                          onClick={() => handleLinkExerciseCard(c.id)}
                        >
                          {linkBusyKey === `urex-${c.id}` ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            t('activityDetail.linkWorkout')
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {(!activityAi?.exercise_payload?.candidates ||
                  activityAi.exercise_payload.candidates.length === 0) &&
                  activityAi?.exercise_payload?.save_draft?.segment_payloads?.length > 0 && (
                  <div className='rounded-xl border border-border bg-muted/20 p-4 space-y-3'>
                    <p className='text-sm text-muted-foreground'>{t('activityDetail.saveAsExerciseHint')}</p>
                    <RunningExerciseDraftPreview
                      splits={activityAi.exercise_payload.save_draft.preview_splits || []}
                      vizHeightPx={48}
                    />
                    <div className='space-y-2'>
                      <Label htmlFor='save-ex-title'>{t('activityDetail.saveExerciseTitle')}</Label>
                      <Input
                        id='save-ex-title'
                        value={saveTitle}
                        onChange={(e) => setSaveTitle(e.target.value)}
                        maxLength={500}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='save-ex-desc'>{t('activityDetail.saveExerciseDescription')}</Label>
                      <Textarea
                        id='save-ex-desc'
                        value={saveDescription}
                        onChange={(e) => setSaveDescription(e.target.value)}
                        rows={3}
                        className='resize-y min-h-[72px]'
                      />
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      <Button
                        type='button'
                        onClick={() => handleSaveAsExercise(activityAi.exercise_payload.save_draft)}
                        disabled={saveSubmitting}
                      >
                        {saveSubmitting ? <Loader2 className='h-4 w-4 animate-spin' /> : t('activityDetail.saveToLibrary')}
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() =>
                          handleSaveAsExercise(activityAi.exercise_payload.save_draft, {
                            addToPlannerLibrary: false,
                            linkActivity: true,
                          })
                        }
                        disabled={saveSubmitting}
                      >
                        {t('activityDetail.linkActivityOnly')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Route Map */}
        {(activity.polyline || activity.summary_polyline) && (
          <div className='mb-6'>
            <h2 className='text-lg font-semibold text-foreground flex items-center gap-2 mb-3'>
              <MapPin className='h-5 w-5 text-primary' />
              {t('activityDetail.route')}
            </h2>
            <ActivityMap
              encodedPolyline={activity.polyline}
              summaryPolyline={activity.summary_polyline}
              streams={streams}
              height={420}
            />
          </div>
        )}

        {showSeparatorBeforeDetailBlocks && <Separator className='my-6' />}

        {/* Laps */}
        {laps && laps.length > 0 && !isWeightTraining && (
          <div className='mb-6'>
            <div className='flex flex-wrap items-center justify-between gap-3 mb-3'>
              <h2 className='text-lg font-semibold text-foreground flex items-center gap-2'>
                <Timer className='h-5 w-5 text-primary' />
                {t('activityDetail.laps')} ({displayLaps.length})
              </h2>
              <div className='flex flex-wrap items-center gap-2'>
                {hasPlannedLapsView && (
                  <div className='flex items-center gap-1 rounded-md border border-border p-0.5 bg-muted/30'>
                    <Button
                      type='button'
                      variant={lapsViewSource === 'workout' ? 'default' : 'ghost'}
                      size='sm'
                      className='h-7 text-xs'
                      onClick={() => setLapsViewSource('workout')}
                    >
                      {t('activityDetail.lapsViewWorkoutLaps')}
                    </Button>
                    <Button
                      type='button'
                      variant={lapsViewSource === 'device' ? 'default' : 'ghost'}
                      size='sm'
                      className='h-7 text-xs'
                      onClick={() => setLapsViewSource('device')}
                    >
                      {t('activityDetail.lapsViewDeviceLaps')} ({laps.length})
                    </Button>
                  </div>
                )}
                {displayLaps.length > 5 && (
                  <Button variant='ghost' size='sm' onClick={() => setShowAllLaps(v => !v)}>
                    {showAllLaps ? <ChevronUp className='h-4 w-4 mr-1' /> : <ChevronDown className='h-4 w-4 mr-1' />}
                    {showAllLaps ? t('activityDetail.showLess') : t('activityDetail.showAll')}
                  </Button>
                )}
              </div>
            </div>
            {hasPlannedLapsView && lapsViewSource === 'workout' && (
              <p className='text-xs text-muted-foreground mb-2'>{t('activityDetail.lapsAlignedHint')}</p>
            )}
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-border text-muted-foreground'>
                    <th className='text-left py-2 pr-4 font-medium'>#</th>
                    <th className='text-right py-2 px-2 font-medium'>{t('athlete.distance')}</th>
                    <th className='text-right py-2 px-2 font-medium'>{t('athlete.duration')}</th>
                    <th className='text-right py-2 px-2 font-medium'>{t('athlete.pace')}</th>
                    <th className='text-right py-2 px-2 font-medium'>{t('athlete.heartRate')}</th>
                    <th className='text-right py-2 px-2 font-medium'>{t('activityDetail.avgPower')}</th>
                    <th className='text-right py-2 pl-2 font-medium'>{t('athlete.elevation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllLaps ? displayLaps : displayLaps.slice(0, 5)).map((lap) => (
                    <tr key={lap.id ?? `lap-${lap.lap_index}`} className='border-b border-border/50 hover:bg-accent/30 transition-colors'>
                      <td className='py-2 pr-4 text-muted-foreground'>{lap.lap_index}</td>
                      <td className='text-right py-2 px-2'>{formatDistanceKm(lap.distance)}</td>
                      <td className='text-right py-2 px-2'>{formatDuration(lap.moving_time)}</td>
                      <td className='text-right py-2 px-2'>{formatPace(lap.average_speed)}</td>
                      <td className='text-right py-2 px-2'>
                        {lap.average_heartrate ? `${Math.round(parseFloat(lap.average_heartrate))}` : '-'}
                      </td>
                      <td className='text-right py-2 px-2'>
                        {lap.average_watts != null &&
                        Number.isFinite(parseFloat(lap.average_watts)) &&
                        parseFloat(lap.average_watts) > 0
                          ? `${Math.round(parseFloat(lap.average_watts))} ${t('units.powerW')}`
                          : '-'}
                      </td>
                      <td className='text-right py-2 pl-2'>{formatElevation(lap.total_elevation_gain)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Splits */}
        {splits &&
          !isWeightTraining &&
          (splits.metric?.length > 0 || splits.standard?.length > 0) && (
          <div className='mb-6'>
            <div className='flex items-center justify-between mb-3'>
              <h2 className='text-lg font-semibold text-foreground flex items-center gap-2'>
                <Activity className='h-5 w-5 text-primary' />
                {t('activityDetail.splits')}
              </h2>
              <div className='flex gap-1'>
                <Button
                  variant={splitUnit === 'metric' ? 'default' : 'outline'}
                  size='sm'
                  className='h-7 text-xs'
                  onClick={() => setSplitUnit('metric')}
                >
                  km
                </Button>
                <Button
                  variant={splitUnit === 'standard' ? 'default' : 'outline'}
                  size='sm'
                  className='h-7 text-xs'
                  onClick={() => setSplitUnit('standard')}
                >
                  mi
                </Button>
              </div>
            </div>
            <SplitBars splits={splits[splitUnit] || []} t={t} />
          </div>
        )}

        {/* Performance Charts */}
        {streams && !isWeightTraining && Object.keys(streams).length > 0 && (
          <div className='mb-6'>
            <h2 className='text-lg font-semibold text-foreground flex items-center gap-2 mb-1'>
              <TrendingUp className='h-5 w-5 text-primary' />
              {t('activityDetail.performanceData')}
            </h2>
            {!!plannedOverlay?.linked && (
              <p className='text-xs text-primary mb-3'>{t('activityDetail.plannedWorkoutOverlay')}</p>
            )}
            <StreamCharts
              streams={streams}
              laps={laps}
              activity={activity}
              plannedBoundarySecs={plannedBoundarySecs}
            />
          </div>
        )}

        <ActivitySkillStimulusCard activityId={activity.id} sportType={activity.sport_type || activity.type} />

        {/* Segment Efforts */}
        {segmentEfforts && segmentEfforts.length > 0 && (
          <div className='mb-6'>
            <h2 className='text-lg font-semibold text-foreground flex items-center gap-2 mb-3'>
              <Zap className='h-5 w-5 text-primary' />
              {t('activityDetail.segments')} ({segmentEfforts.length})
            </h2>
            <div className='space-y-2'>
              {segmentEfforts.map((effort) => (
                <div
                  key={effort.id}
                  className='flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-accent/30 transition-colors'
                >
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium truncate text-foreground'>{effort.name || effort.segment_name}</p>
                    <div className='flex gap-3 text-xs text-muted-foreground mt-1'>
                      <span>{formatDistanceKm(effort.distance)}</span>
                      <span>{formatDuration(effort.elapsed_time)}</span>
                      {effort.average_grade != null && <span>{parseFloat(effort.average_grade).toFixed(1)}% {t('activityDetail.grade')}</span>}
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    {effort.pr_rank && (
                      <Badge variant='outline' className='text-xs bg-amber-500/10 text-amber-600 border-amber-500/20'>
                        PR #{effort.pr_rank}
                      </Badge>
                    )}
                    {effort.kom_rank && (
                      <Badge variant='outline' className='text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20'>
                        KOM #{effort.kom_rank}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!activity.detail_synced && (
          <div className='text-center py-8'>
            <p className='text-sm text-muted-foreground mb-3'>
              {t('activityDetail.notSynced')}
            </p>
            <Button onClick={handleResync} disabled={syncing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {t('activityDetail.syncNow')}
            </Button>
          </div>
        )}
      </div>

      <KpiTrendModal
        isOpen={kpiModalOpen}
        onClose={() => {
          setKpiModalOpen(false);
          setKpiModalCardValues(null);
        }}
        initialKpi={kpiModalKpi}
        currentActivityId={kpiModalActivityId ?? activity?.id}
        activityKpiFromCard={kpiModalCardValues}
      />

      <ActivityFindSimilarModal
        open={similarOpen}
        onOpenChange={setSimilarOpen}
        currentActivityId={id}
        prefillDistanceMeters={
          data?.activity?.distance != null ? parseFloat(data.activity.distance) : null
        }
        t={t}
        onCompareSuggested={handleCompareFromSuggested}
        onCompareBrowse={handleCompareFromBrowse}
      />

      <Dialog open={compareOpen} onOpenChange={setCompareOpen} modal={!kpiModalOpen}>
        <DialogContent
          className='max-w-[90vw] sm:max-w-[85vw] max-h-[90vh] overflow-hidden flex flex-col'
          priority='high'
          onPointerDownOutside={(e) => kpiModalOpen && e.preventDefault()}
          onInteractOutside={(e) => kpiModalOpen && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Scale className='h-5 w-5' />
              {t('activityDetail.compareActivities')}
            </DialogTitle>
          </DialogHeader>
          {compareActivity ? (
            <CompareModalContent
              currentActivity={data?.activity}
              compareActivity={compareActivity?.activity ?? compareActivity}
              similarActivities={similarActivities}
              compareIndex={compareIndex}
              compareLoading={compareLoading}
              onComparePrev={handleComparePrev}
              onCompareNext={handleCompareNext}
              t={t}
              onOpenActivity={(id) => { setCompareOpen(false); navigate(`/activity/${id}`); }}
              onKpiClick={(key, activityId) => {
                setKpiModalKpi(key);
                setKpiModalActivityId(activityId);
                const src = Number(activityId) === Number(data?.activity?.id)
                  ? data?.activity
                  : (compareActivity?.activity ?? compareActivity);
                setKpiModalCardValues(buildKpiCardValueMap(src, t));
                setKpiModalOpen(true);
              }}
            />
          ) : compareLoading ? (
            <div className='flex items-center justify-center py-16'>
              <Loader2 className='h-10 w-10 animate-spin text-primary' />
            </div>
          ) : (
            <p className='text-sm text-muted-foreground py-8 text-center'>
              {t('activityDetail.compareLoadError')}
            </p>
          )}
        </DialogContent>
      </Dialog>

      <RunningExerciseDetail
        open={linkedWorkoutViewOpen}
        onOpenChange={setLinkedWorkoutViewOpen}
        mode='instance'
        instanceId={linkedRunningExercise?.id ?? null}
        fallbackTitle={linkedRunningExercise?.title}
      />
      <UserRunningExerciseEditorModal
        open={linkedWorkoutEditOpen}
        onOpenChange={setLinkedWorkoutEditOpen}
        exerciseId={linkedRunningExercise?.id ?? null}
        onSaved={handleLinkedWorkoutSaved}
      />

      <Dialog open={healthPainOpen} onOpenChange={setHealthPainOpen}>
        <DialogContent
          className={cn(
            'w-[95vw] max-w-[95vw] h-[95vh] max-h-[95vh] flex flex-col gap-0 p-0 overflow-hidden',
            'translate-x-[-50%] translate-y-[-50%]'
          )}
        >
          <div className='shrink-0 border-b border-border px-6 pt-6 pb-4 pr-14'>
            <DialogHeader className='space-y-1.5 text-left'>
              <DialogTitle>{t('activityDetail.logPainTitle')}</DialogTitle>
              <DialogDescription>
                {data?.activity?.name
                  ? t('activityDetail.logPainModalDescription', { name: data.activity.name })
                  : t('activityDetail.logPainModalDescriptionNoName')}
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className='flex-1 min-h-0 overflow-y-auto px-6 py-4 flex flex-col'>
            {healthPainOpen ? (
              <Suspense
                fallback={
                  <div className='flex items-center justify-center py-24 text-muted-foreground gap-2'>
                    <Loader2 className='h-8 w-8 animate-spin text-primary' />
                  </div>
                }
              >
                <AthleteHealthBodyMapWidget
                  widget={{ name: t('health.dashboard.widgetBodyMapTitle') }}
                  stravaActivityId={id ? parseInt(id, 10) : undefined}
                />
              </Suspense>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={sessionChatModalOpen} onOpenChange={setSessionChatModalOpen}>
        <DialogContent
          className={cn(
            'w-[min(960px,92vw)] max-w-[min(960px,92vw)] h-[min(720px,88vh)] max-h-[88vh] p-0 gap-0 flex flex-col overflow-hidden',
            'translate-x-[-50%] translate-y-[-50%]'
          )}
        >
          <div className='shrink-0 border-b border-border px-4 pt-4 pb-3 pr-14'>
            <DialogHeader className='space-y-1 text-left'>
              <DialogTitle>{t('activityDetail.sessionAiChatDialogTitle')}</DialogTitle>
              <DialogDescription>{t('activityDetail.sessionAiChatDialogDescription')}</DialogDescription>
            </DialogHeader>
          </div>
          <div className='flex-1 min-h-0 flex flex-col overflow-hidden'>
            {sessionChatModalOpen && id ? (
              <Suspense
                fallback={
                  <div className='flex flex-1 items-center justify-center gap-2 text-muted-foreground py-16'>
                    <Loader2 className='h-8 w-8 animate-spin text-primary' />
                  </div>
                }
              >
                <AgentChatWidget
                  widget={{ widget_key: 'athlete_agent_chat' }}
                  embeddedStravaActivityId={parseInt(id, 10)}
                  embeddedActivityTitle={data?.activity?.name || ''}
                />
              </Suspense>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={activityEditOpen}
        onOpenChange={(open) => {
          setActivityEditOpen(open);
          if (!open) setActivityEditError(null);
        }}
      >
        <DialogContent className='max-w-[min(90vw,32rem)]'>
          <DialogHeader>
            <DialogTitle>{t('activityDetail.editDialogTitle')}</DialogTitle>
            <DialogDescription>{t('activityDetail.editDialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-1'>
            <div className='space-y-2'>
              <Label htmlFor='activity-edit-title'>{t('activityDetail.editTitleLabel')}</Label>
              <Input
                id='activity-edit-title'
                value={activityEditName}
                onChange={(e) => setActivityEditName(e.target.value)}
                maxLength={255}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='activity-edit-description'>{t('activityDetail.editDescriptionLabel')}</Label>
              <Textarea
                id='activity-edit-description'
                value={activityEditDescription}
                onChange={(e) => setActivityEditDescription(e.target.value)}
                rows={4}
                className='resize-y min-h-[88px]'
              />
            </div>
            <div className='flex items-start justify-between gap-3 rounded-lg border border-border p-3'>
              <div className='space-y-1 pr-2'>
                <Label htmlFor='activity-edit-race' className='text-sm font-medium'>
                  {t('activityDetail.editMarkAsRace')}
                </Label>
                <p className='text-xs text-muted-foreground leading-snug'>
                  {t('activityDetail.editMarkAsRaceHint')}
                </p>
              </div>
              <Switch
                id='activity-edit-race'
                checked={activityEditIsRace}
                onCheckedChange={setActivityEditIsRace}
                disabled={activityEditSaving}
              />
            </div>
            {!!activityEditError && (
              <p className='text-sm text-destructive' role='alert'>
                {activityEditError}
              </p>
            )}
          </div>
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setActivityEditOpen(false)}
              disabled={activityEditSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button type='button' onClick={saveActivityEdit} disabled={activityEditSaving}>
              {activityEditSaving ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  {t('common.saving')}
                </>
              ) : (
                t('activityDetail.editSave')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeleteError(null);
        }}
      >
        <DialogContent className='max-w-[min(90vw,28rem)]'>
          <DialogHeader>
            <DialogTitle>{t('activityDetail.deleteDialogTitle')}</DialogTitle>
            <DialogDescription className='space-y-2'>
              <span className='block'>{t('activityDetail.deleteDialogDescription')}</span>
              {data?.activity?.strava_activity_id != null && (
                <a
                  href={`https://www.strava.com/activities/${String(data.activity.strava_activity_id)}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center gap-1 text-sm font-medium text-primary underline underline-offset-4'
                >
                  <ExternalLink className='h-3.5 w-3.5' aria-hidden />
                  {t('activityDetail.deleteOpenStrava')}
                </a>
              )}
            </DialogDescription>
          </DialogHeader>
          {!!deleteError && (
            <p className='text-sm text-destructive' role='alert'>
              {deleteError}
            </p>
          )}
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deletingActivity}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type='button'
              variant='destructive'
              onClick={handleDeleteActivity}
              disabled={deletingActivity}
            >
              {deletingActivity ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  {t('activityDetail.deleting')}
                </>
              ) : (
                t('activityDetail.delete')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const KPI_HIGHER_IS_BETTER = { re: true, stride: true, paceBpm: false, bpmWatt: false, paceWatt: false, elevKm: false, calKm: false };

function CompareModalContent({ currentActivity, compareActivity, similarActivities = [], compareIndex = 0, compareLoading = false, onComparePrev, onCompareNext, t, onOpenActivity, onKpiClick }) {
  const { language } = useLanguage();
  const dateLocale = language === 'no' ? nb : undefined;

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: dateLocale });
    } catch {
      return '';
    }
  };

  const formatRelativeToCurrent = (compareDateStr, currentDateStr) => {
    if (!compareDateStr || !currentDateStr) return '';
    try {
      const compare = new Date(compareDateStr);
      const current = new Date(currentDateStr);
      const dist = formatDistance(current, compare, { addSuffix: false, locale: dateLocale });
      return compare > current ? t('activityDetail.compareTimeAfter', { dist }) : t('activityDetail.compareTimeBefore', { dist });
    } catch {
      return '';
    }
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, { dateStyle: 'medium' });
    } catch {
      return '—';
    }
  };

  const comparePaceStr = (speed) => {
    const sp = parseFloat(speed);
    if (!Number.isFinite(sp) || sp <= 0) return null;
    const paceSec = 1000 / sp;
    if (!Number.isFinite(paceSec)) return null;
    const min = Math.floor(paceSec / 60);
    const sec = Math.floor(paceSec % 60);
    return `${min}:${sec.toString().padStart(2, '0')} /km`;
  };

  const rows = [
    {
      key: 'distance',
      label: t('activityDetail.distance'),
      icon: MapPin,
      value: (a) => {
        const m = parseFloat(a?.distance);
        if (!Number.isFinite(m) || m <= 0) return null;
        return `${(m / 1000).toFixed(2)} km`;
      },
    },
    {
      key: 'duration',
      label: t('activityDetail.duration'),
      icon: Clock,
      value: (a) => {
        const mt = parseFloat(a?.moving_time);
        if (!Number.isFinite(mt) || mt < 0) return null;
        return formatDuration(mt);
      },
    },
    {
      key: 'pace',
      label: t('activityDetail.pace'),
      icon: Gauge,
      value: (a) => comparePaceStr(a?.average_speed),
    },
    {
      key: 'elevation',
      label: t('activityDetail.elevation'),
      icon: Mountain,
      value: (a) => {
        const e = parseFloat(a?.total_elevation_gain);
        if (!Number.isFinite(e) || e <= 0) return null;
        return `${Math.round(e)} m`;
      },
    },
    {
      key: 'heartrate',
      label: t('activityDetail.heartrate'),
      icon: Heart,
      value: (a) => {
        const hr = parseFloat(a?.average_heartrate);
        if (!Number.isFinite(hr) || hr <= 0) return null;
        return `${Math.round(hr)} bpm`;
      },
    },
    {
      key: 'calories',
      label: t('activityDetail.calories'),
      icon: Flame,
      value: (a) => {
        const c = parseFloat(a?.calories);
        if (!Number.isFinite(c) || c <= 0) return null;
        return `${Math.round(c)}`;
      },
    },
    {
      key: 'suffer',
      label: t('activityDetail.sufferScore'),
      icon: HeartPulse,
      value: (a) => {
        const s = parseFloat(a?.suffer_score);
        if (!Number.isFinite(s) || s <= 0) return null;
        return String(Math.round(s));
      },
    },
  ];

  const currentKpis = computeKpis(currentActivity, t);
  const compareKpis = computeKpis(compareActivity, t);
  const allKpiKeys = [...new Set([...currentKpis.map((k) => k.key), ...compareKpis.map((k) => k.key)])];
  const kpiMap = (arr) => Object.fromEntries(arr.map((k) => [k.key, k]));

  const currentKpiMap = kpiMap(currentKpis);
  const compareKpiMap = kpiMap(compareKpis);

  const ActivityCard = ({ activity, variant = 'default' }) => {
    const isHighlight = variant === 'highlight';
    if (!activity) {
      return (
        <div className='rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center'>
          <p className='text-sm text-muted-foreground'>{t('activityDetail.compareLoadError')}</p>
        </div>
      );
    }
    return (
      <div
        className={cn(
          'rounded-xl overflow-hidden shadow-sm transition-shadow hover:shadow-md',
          isHighlight
            ? 'border-2 border-primary/50 bg-gradient-to-b from-primary/8 to-primary/3 ring-1 ring-primary/20'
            : 'border border-border bg-card'
        )}
      >
        <div className='p-5 space-y-4'>
          <div className='flex items-start justify-between gap-3'>
            <div className='min-w-0 flex-1'>
              <h3 className='font-semibold text-base text-foreground leading-tight line-clamp-2'>
                {activity?.name || t('activityDetail.untitled')}
              </h3>
              <p className='text-xs text-muted-foreground mt-1'>
                {formatDate(activity?.start_date_local || activity?.start_date)}
              </p>
            </div>
            <Button
              variant={isHighlight ? 'default' : 'outline'}
              size='sm'
              className='shrink-0'
              onClick={() => onOpenActivity(activity?.id)}
            >
              <ExternalLink className='h-3.5 w-3.5 mr-1.5' />
              {t('activityDetail.openActivity')}
            </Button>
          </div>

          {(activity?.summary_polyline || activity?.polyline) && (
            <div className='h-32 rounded-lg overflow-hidden border border-border shadow-inner'>
              <ActivityMap
                summaryPolyline={activity.summary_polyline || activity.polyline}
                height={128}
              />
            </div>
          )}

          <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
            {rows.map((r) => {
              const Icon = r.icon;
              const display = r.value(activity);
              if (display == null) return null;
              return (
                <div key={r.key} className='flex items-center gap-2'>
                  <Icon className='h-4 w-4 text-muted-foreground shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs text-muted-foreground truncate'>{r.label}</p>
                    <p className='text-sm font-semibold text-foreground'>{display}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {((variant === 'default' ? currentKpis : compareKpis).length > 0) && (
            <div className='pt-4 border-t border-border'>
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2'>
                {t('activityDetail.kpi.re')} & KPIs
              </p>
              <div className='flex flex-wrap gap-2'>
                {(variant === 'default' ? currentKpis : compareKpis).map((k) => (
                  <button
                    key={k.key}
                    type='button'
                    onClick={() => onKpiClick?.(k.key, activity.id)}
                    className='inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border cursor-pointer hover:opacity-90 transition-opacity text-left'
                    style={{
                      borderColor: `${k.color}40`,
                      backgroundColor: `${k.color}18`,
                      color: 'inherit',
                    }}
                    title={k.hint || ''}
                  >
                    <span className='w-1.5 h-1.5 rounded-full shrink-0' style={{ backgroundColor: k.color }} />
                    {k.value} {k.unit}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const compareKey = compareActivity?.id ?? 'empty';
  const fadeTransition = { duration: 0.2 };

  return (
    <div className='overflow-y-auto flex-1 min-h-0 pr-2'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        <ActivityCard activity={currentActivity} variant='default' />
        <div className='min-h-[200px]'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={compareKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={fadeTransition}
              className='h-full'
            >
              <ActivityCard activity={compareActivity} variant='highlight' />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {allKpiKeys.length > 0 && (
        <div className='mt-6 rounded-xl border border-border bg-muted/30 p-5 shadow-sm'>
          <div className='flex items-center justify-between gap-4 mb-4'>
            <h4 className='font-semibold text-sm text-foreground flex items-center gap-2'>
              <Activity className='h-4 w-4 text-primary' />
              {t('activityDetail.compareActivities')} — KPI comparison
            </h4>
            {similarActivities.length > 1 && (
              <div className='flex items-center gap-2 shrink-0'>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8 w-8 p-0'
                  onClick={onComparePrev}
                  disabled={compareIndex <= 0 || compareLoading || !onComparePrev}
                  title={t('activityDetail.comparePrev')}
                >
                  <ChevronLeft className='h-4 w-4' />
                </Button>
                <span className='text-sm text-muted-foreground tabular-nums min-w-[3rem] text-center'>
                  {compareIndex + 1} / {similarActivities.length}
                </span>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8 w-8 p-0'
                  onClick={onCompareNext}
                  disabled={compareIndex >= similarActivities.length - 1 || compareLoading || !onCompareNext}
                  title={t('activityDetail.compareNext')}
                >
                  <ChevronRight className='h-4 w-4' />
                </Button>
              </div>
            )}
          </div>
          <div className='overflow-x-auto rounded-lg border border-border bg-background'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-border/50 bg-muted/50'>
                  <th className='text-left py-2 px-4 text-muted-foreground font-medium'>Metric</th>
                  <th className='text-right py-2 px-4 text-muted-foreground font-medium'>
                    <span className='inline-flex flex-col items-end gap-0.5'>
                      <span className='inline-flex items-center gap-1.5 justify-end'>
                        <Activity className='h-4 w-4 shrink-0' />
                        <span className='truncate max-w-[200px]' title={currentActivity?.name}>
                          {currentActivity?.name || t('activityDetail.untitled')}
                        </span>
                      </span>
                      {currentActivity?.start_date_local || currentActivity?.start_date ? (
                        <span className='text-[10px] text-muted-foreground/80'>
                          {formatTimeAgo(currentActivity?.start_date_local || currentActivity?.start_date)}
                        </span>
                      ) : null}
                    </span>
                  </th>
                  <th className='text-right py-2 px-2 w-16 text-muted-foreground font-medium' />
                  <th className='text-right py-2 px-4 text-muted-foreground font-medium'>
                    <AnimatePresence mode='wait'>
                      <motion.span
                        key={compareKey}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={fadeTransition}
                        className='inline-flex flex-col items-end gap-0.5'
                      >
                        <span className='inline-flex items-center gap-1.5 justify-end'>
                          <Scale className='h-4 w-4 shrink-0' />
                          <span className='truncate max-w-[200px]' title={compareActivity?.name}>
                            {compareActivity?.name || t('activityDetail.untitled')}
                          </span>
                        </span>
                        {(currentActivity?.start_date_local || currentActivity?.start_date) && (compareActivity?.start_date_local || compareActivity?.start_date) ? (
                          <span className='text-[10px] text-muted-foreground/80'>
                            {formatRelativeToCurrent(compareActivity?.start_date_local || compareActivity?.start_date, currentActivity?.start_date_local || currentActivity?.start_date)}
                          </span>
                        ) : null}
                      </motion.span>
                    </AnimatePresence>
                  </th>
                  <th className='text-right py-2 px-2 w-16 text-muted-foreground font-medium' />
                </tr>
              </thead>
              <tbody>
                {allKpiKeys.map((key) => {
                  const cur = currentKpiMap[key];
                  const cmp = compareKpiMap[key];
                  const label = cur?.label || cmp?.label || key;
                  const curVal = cur ? parseFloat(cur.value) : NaN;
                  const cmpVal = cmp ? parseFloat(cmp.value) : NaN;
                  const higherBetter = KPI_HIGHER_IS_BETTER[key] ?? true;
                  const curBetter = !Number.isNaN(curVal) && !Number.isNaN(cmpVal) && (higherBetter ? curVal > cmpVal : curVal < cmpVal);
                  const cmpBetter = !Number.isNaN(curVal) && !Number.isNaN(cmpVal) && (higherBetter ? cmpVal > curVal : cmpVal < curVal);
                  const pctCur = curBetter && cmpVal !== 0 ? Math.abs(((curVal - cmpVal) / cmpVal) * 100) : 0;
                  const pctCmp = cmpBetter && curVal !== 0 ? Math.abs(((cmpVal - curVal) / curVal) * 100) : 0;

                  return (
                    <tr key={key} className='border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors'>
                      <td className='py-2.5 px-4 text-muted-foreground'>{label}</td>
                      <td className='py-2.5 px-4 text-right font-medium text-foreground'>{cur ? `${cur.value} ${cur.unit}` : '—'}</td>
                      <td className='py-2.5 px-2 w-16 text-right'>
                        {curBetter && pctCur > 0 ? (
                          <span className='text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-500/20 text-green-700 dark:text-green-400'>
                            +{pctCur.toFixed(1)}%
                          </span>
                        ) : null}
                      </td>
                      <td className='py-2.5 px-4 text-right font-medium text-foreground'>
                        <AnimatePresence mode='wait'>
                          <motion.span key={compareKey} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={fadeTransition} className='inline-block'>
                            {cmp ? `${cmp.value} ${cmp.unit}` : '—'}
                          </motion.span>
                        </AnimatePresence>
                      </td>
                      <td className='py-2.5 px-2 w-16 text-right'>
                        <AnimatePresence mode='wait'>
                          <motion.span key={compareKey} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={fadeTransition} className='inline-block'>
                            {cmpBetter && pctCmp > 0 ? (
                              <span className='text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-500/20 text-green-700 dark:text-green-400'>
                                +{pctCmp.toFixed(1)}%
                              </span>
                            ) : null}
                          </motion.span>
                        </AnimatePresence>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiRow({ activity, t, trends = {}, onKpiClick }) {
  const kpis = computeKpis(activity, t);
  if (kpis.length === 0) return null;

  return (
    <div className='flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-thin'>
      {kpis.map((kpi) => {
        const trend = trends[kpi.key];
        const rawDelta = trend?.rawDeltaPct;
        const trendFlat =
          trend &&
          trend.pctLabel != null &&
          (rawDelta == null || !Number.isFinite(rawDelta) || Math.abs(rawDelta) < 0.05);
        const trendGood = trend && trend.pctLabel != null && !trendFlat && trend.semanticGood === true;
        const trendBad = trend && trend.pctLabel != null && !trendFlat && trend.semanticGood === false;
        const trendClass = trendGood
          ? 'text-emerald-600 dark:text-emerald-400'
          : trendBad
            ? 'text-red-600 dark:text-red-400'
            : 'text-muted-foreground';

        const trendIcon =
          trend && trend.pctLabel != null
            ? trendFlat ? (
              <Minus className='h-3 w-3 shrink-0' aria-hidden />
            ) : rawDelta > 0 ? (
              <TrendingUp className='h-3 w-3 shrink-0' aria-hidden />
            ) : (
              <TrendingDown className='h-3 w-3 shrink-0' aria-hidden />
            )
            : null;

        return (
          <button
            key={kpi.key}
            type='button'
            onClick={() => onKpiClick?.(kpi.key)}
            className='flex w-max max-w-[min(26rem,92vw)] shrink-0 flex-col items-stretch gap-1 px-3 py-2 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors cursor-pointer text-left sm:flex-row sm:items-center sm:gap-2'
            title={kpi.hint || ''}
            style={{ borderLeftWidth: 3, borderLeftColor: kpi.color }}
          >
            <div className='flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5'>
              <span className='text-xs text-muted-foreground leading-snug'>{kpi.label}</span>
              <span className='text-sm font-bold text-foreground tabular-nums'>{kpi.value}</span>
              {!!kpi.unit && (
                <span className='text-xs text-muted-foreground whitespace-nowrap'>{kpi.unit}</span>
              )}
            </div>
            {trend && trend.pctLabel != null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      'flex shrink-0 items-center gap-0.5 self-center sm:ml-auto text-[10px] font-medium leading-none',
                      trendClass,
                      trendGood && 'hover:text-emerald-500 dark:hover:text-emerald-300',
                      trendBad && 'hover:text-red-500 dark:hover:text-red-300',
                      trendFlat && 'hover:text-foreground/90'
                    )}
                  >
                    {trendIcon}
                    <span>{trend.pctLabel}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side='top'
                  className='max-w-[min(340px,92vw)] text-xs text-left whitespace-pre-line'
                >
                  {trend.tooltip}
                </TooltipContent>
              </Tooltip>
            )}
          </button>
        );
      })}
    </div>
  );
}

function splitAverageWatts(split) {
  const w = split.average_watts != null ? parseFloat(split.average_watts) : NaN;
  return Number.isFinite(w) && w > 0 ? Math.round(w) : null;
}

function SplitBars({ splits, t }) {
  if (!splits || splits.length === 0) return null;

  const paces = splits
    .map(s => (s.average_speed && parseFloat(s.average_speed) > 0) ? 1000 / parseFloat(s.average_speed) : null)
    .filter(Boolean);

  if (paces.length === 0) return null;

  const minPace = Math.min(...paces);
  const maxPace = Math.max(...paces);
  const range = maxPace - minPace || 1;

  const dash = '-';
  const gridCols =
    'grid grid-cols-[1.5rem_minmax(0,1fr)_3rem_3.5rem_minmax(3.5rem,max-content)] gap-x-2 items-center text-xs';

  return (
    <div className='space-y-1'>
      <div className={`${gridCols} border-b border-border/60 pb-1 text-[0.7rem] sm:text-xs font-medium text-muted-foreground`}>
        <span aria-hidden className='text-right tabular-nums'>#</span>
        <span className='min-w-0' aria-hidden />
        <span className='text-right tabular-nums'>{t('activityDetail.pace')}</span>
        <span className='text-right tabular-nums'>{t('activityDetail.splitsColHr')}</span>
        <span className='text-right tabular-nums'>{t('activityDetail.avgPower')}</span>
      </div>
      {splits.map((split) => {
        const pace = (split.average_speed && parseFloat(split.average_speed) > 0)
          ? 1000 / parseFloat(split.average_speed) : null;
        const pct = pace ? Math.max(20, 100 - ((pace - minPace) / range) * 70) : 20;
        const mins = pace ? Math.floor(pace / 60) : 0;
        const secs = pace ? Math.round(pace % 60) : 0;
        const paceLabel = pace ? `${mins}:${secs.toString().padStart(2, '0')}` : dash;
        const hrVal =
          split.average_heartrate && parseFloat(split.average_heartrate) > 0
            ? Math.round(parseFloat(split.average_heartrate))
            : null;
        const wattsVal = splitAverageWatts(split);

        return (
          <div key={split.split_index} className={gridCols}>
            <span className='text-right text-muted-foreground tabular-nums'>{split.split_index}</span>
            <div
              className='min-h-5 min-w-0 overflow-hidden rounded bg-muted/30'
              aria-hidden
              title={t('activityDetail.splitsBarAria')}
            >
              <div
                className='h-5 max-w-full rounded bg-primary/70 transition-all'
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className='text-right font-medium tabular-nums text-foreground'>{paceLabel}</span>
            <span className='text-right tabular-nums text-muted-foreground'>
              {hrVal != null ? `${hrVal} ${t('units.bpm')}` : dash}
            </span>
            <span className='text-right tabular-nums text-muted-foreground'>
              {wattsVal != null ? `${wattsVal} ${t('units.powerW')}` : dash}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className='bg-card border border-border rounded-lg p-2 sm:p-3 text-center min-w-0'>
    <div className='flex justify-center mb-1.5 text-primary [&>svg]:h-4 [&>svg]:w-4'>{icon}</div>
    <p className='text-base sm:text-lg font-bold text-foreground tabular-nums truncate' title={typeof value === 'string' ? value : undefined}>{value}</p>
    <p className='text-[10px] sm:text-xs text-muted-foreground leading-tight break-words'>{label}</p>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className='bg-muted/30 rounded-md px-3 py-2'>
    <p className='text-xs text-muted-foreground'>{label}</p>
    <p className='text-sm font-medium text-foreground'>{value}</p>
  </div>
);

function formatDistanceKm(meters) {
  const m = parseFloat(meters);
  if (!Number.isFinite(m) || m <= 0) return '—';
  return `${(m / 1000).toFixed(2)} km`;
}

function formatPace(speed) {
  const sp = parseFloat(speed);
  if (!Number.isFinite(sp) || sp === 0) return '—';
  const paceSeconds = 1000 / sp;
  if (!Number.isFinite(paceSeconds)) return '—';
  const mins = Math.floor(paceSeconds / 60);
  const secs = Math.round(paceSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')} /km`;
}

function formatElevation(meters) {
  const m = parseFloat(meters);
  if (!Number.isFinite(m)) return '—';
  return `${Math.round(m)} m`;
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default ActivityDetail;
